import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/components/ui/helpers/toast';

import { toastRecoveryOutcome } from '../../helpers/toast-recovery-outcome';
import { RECOVERY_REASON } from './types/subscription-recovery-enums';
import { useSubscriptionRecoveryReason } from '../../hooks/use-subscription-recovery-reason'

import { BANNER_TONE } from '@/components/ui/types/banner-variants-enums';
import { Banner } from '@/components/ui/banner';
import { BUTTON_SIZE } from '@/components/ui/types/button-variants-enums';
import { Button } from '@/components/ui/button';
import { ICON_BUTTON_SIZE, ICON_BUTTON_VARIANT } from '@/components/ui/types/icon-button-variants-enums';
import { IconButton } from '@/components/ui/icon-button';
import { useShouldDeferBillingToOnboarding } from '@/features/billing/hooks/use-should-defer-billing-to-onboarding';
import { useDismissibleBanner } from '@/hooks/browser/use-dismissible-banner';
import { invalidateUserData } from '@/lib/query/cache-invalidation';
import { AddPaymentMethodModal } from '@/features/billing/components/payment-methods/add-payment-method-modal';
import { RECOVERY_INVOICE_RETRY_MODAL_PROPS } from '@/features/billing/constants/billing-recovery-constants';
import type { CompleteStripeSetupIntentResponse } from '@/services/internal-api/subscriptions/types/subscription-api-service-types';

const DISMISSAL_TTL_MS = 24 * 60 * 60 * 1000;

const BANNER_MESSAGE: Record<RECOVERY_REASON, string> = {
  [RECOVERY_REASON.PENDING]: 'Your subscription is not yet active. Please add a payment method to activate your plan.',
  [RECOVERY_REASON.PAST_DUE]: 'Your last payment didn\'t go through. Add a new payment method to avoid service interruption.',
  [RECOVERY_REASON.UNPAID]: 'Your subscription has unpaid invoices. Add a new payment method to avoid service interruption.',
  [RECOVERY_REASON.NO_PAYMENT_METHOD]: 'No payment method on file. Add one now to keep your subscription active.',
  [RECOVERY_REASON.CARD_EXPIRED]: 'Your default card has expired. Add a new payment method to avoid service interruption.',
  [RECOVERY_REASON.CARD_EXPIRING_SOON]: 'Your default card is expiring soon. Add a new payment method to avoid service interruption.',
};

const isInvoiceRetryReason = (reason: RECOVERY_REASON) => reason === RECOVERY_REASON.PAST_DUE || reason === RECOVERY_REASON.UNPAID;

export function SubscriptionRecoveryBanner() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const queryClient = useQueryClient();
  const reason = useSubscriptionRecoveryReason();
  // During a trial with incomplete onboarding, a soft payment nudge defers to
  // onboarding — getting the merchant started takes priority over collecting
  // payment. Genuine failures (past-due/unpaid/pending) still surface.
  const deferToOnboarding = useShouldDeferBillingToOnboarding(reason);
  // Re-surface when the recovery reason changes (e.g. card-expiring-soon
  // → past-due) and again every 24h regardless.
  const { isDismissed, dismiss } = useDismissibleBanner({
    bannerId: 'subscription-recovery',
    signal: reason,
    ttlMs: DISMISSAL_TTL_MS,
  });

  if (!reason) return null;
  if (deferToOnboarding) return null;
  if (isDismissed) return null;

  const handleSuccess = (response: CompleteStripeSetupIntentResponse) => {
    // Refetch the account so the banner re-evaluates after the backend has
    // attached the new PM (and, for past_due/unpaid, run the invoice retry).
    invalidateUserData(queryClient);

    // Only the past_due/unpaid triggers drive an actual invoice-retry on the BE — the
    // PM-driven triggers (no-pm, card-expired, card-expiring-soon) hit an already-active
    // sub. For those, getting here = PM attached = success. Don't branch on
    // subscriptionActivated since there's nothing being activated to begin with.
    if (!isInvoiceRetryReason(reason)) {
      toast.success('Payment method added successfully');
      return;
    }

    toastRecoveryOutcome(response);
  };

  return (
    <>
      <Banner.Root role="alert" tone={BANNER_TONE.DANGER}>
        <Banner.Content>
          <Banner.Icon>
            <AlertTriangle />
          </Banner.Icon>
          <Banner.Message>{BANNER_MESSAGE[reason]}</Banner.Message>
        </Banner.Content>
        <Banner.Actions>
          <Button
            aria-label="Add Payment Method"
            variant="inline-link"
            size={BUTTON_SIZE.BANNER}
            className="shrink-0 gap-1"
            onClick={() => setIsModalOpen(true)}
          >
            <Banner.ActionLabel compact="Payment">Add Payment Method</Banner.ActionLabel>
            <ArrowRight aria-hidden="true" className="hidden h-3.5 w-3.5 @[420px]:block" />
          </Button>
        </Banner.Actions>
        <Banner.Dismiss>
          <IconButton
            label="Dismiss billing banner"
            onClick={dismiss}
            size={ICON_BUTTON_SIZE.BANNER}
            variant={ICON_BUTTON_VARIANT.GHOST}
          >
            <X />
          </IconButton>
        </Banner.Dismiss>
      </Banner.Root>

      <AddPaymentMethodModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        onSuccess={handleSuccess}
        makeDefaultPaymentMethod
        analyticsFlow="recovery"
        {...(isInvoiceRetryReason(reason) ? RECOVERY_INVOICE_RETRY_MODAL_PROPS : {})}
      />
    </>
  );
}
